import { css } from 'aphrodite';
import * as React from 'react';
import { AssignmentMethod } from '../../../models/Support/SupportEnums';
import { type ISupportMailbox, useUpdateSupportMailboxMutation } from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { settingsStyles as st } from './styles/settingsStyles';
import { teamTabStyles as styles } from './styles/teamTabStyles';

interface IRoutingDraft {
	isRoutingEnabled: boolean;
	assignmentMethod: AssignmentMethod;
	defaultMaxTickets: number;
}

type RoutingMailboxPatch = Pick<ISupportMailbox, 'isRoutingEnabled' | 'assignmentMethod' | 'defaultMaxTicketsPerAgent'>;

export function TeamTabRoutingSection({ mailbox, mailboxId }: { mailbox: ISupportMailbox; mailboxId: string }) {
	const updateMailboxMutation = useUpdateSupportMailboxMutation(mailboxId);
	const { draft, isDirty, updateDraft, markClean } = useRoutingDraft(mailbox);

	const handleSaveRoutingSettings = () => {
		updateMailboxMutation.mutate(buildRoutingMailboxPatch(draft), {
			onSuccess: markClean,
		});
	};

	return (
		<>
			<div className={css(styles.cardSubTitle, styles.routingTitleSpacing)}>Routing</div>

			<div className={css(st.toggleRow, styles.toggleRowSpaced)}>
				<Button
					type='button'
					variant='ghost'
					styles={[st.toggle, draft.isRoutingEnabled && st.toggleActive]}
					onClick={() => updateDraft({ isRoutingEnabled: !draft.isRoutingEnabled })}
					aria-pressed={draft.isRoutingEnabled}
					aria-label='Auto-assign new tickets'
				>
					<span className={css(st.toggleKnob, draft.isRoutingEnabled && st.toggleKnobActive)} />
				</Button>
				<div className={css(st.toggleText)}>
					<span className={css(st.toggleTitle)}>Auto-assign new tickets</span>
					<span className={css(st.toggleDescription)}>
						Distribute incoming tickets to available agents automatically.
					</span>
				</div>
			</div>

			{draft.isRoutingEnabled && (
				<>
					<div className={css(styles.settingRow)}>
						<div className={css(styles.settingInfo)}>
							<div className={css(styles.settingTitle)}>Assignment method</div>
							<div className={css(styles.settingDesc)}>Balanced fills the lightest workloads first.</div>
						</div>
						<div className={css(styles.segmented)}>
							<Button
								type='button'
								variant='ghost'
								styles={[
									styles.segmentedBtn,
									draft.assignmentMethod === AssignmentMethod.Balanced && styles.segmentedBtnActive,
								]}
								onClick={() => updateDraft({ assignmentMethod: AssignmentMethod.Balanced })}
							>
								Balanced
							</Button>
							<Button
								type='button'
								variant='ghost'
								styles={[
									styles.segmentedBtn,
									draft.assignmentMethod === AssignmentMethod.RoundRobin && styles.segmentedBtnActive,
								]}
								onClick={() => updateDraft({ assignmentMethod: AssignmentMethod.RoundRobin })}
							>
								Round robin
							</Button>
						</div>
					</div>

					<div className={css(styles.settingRow)}>
						<div className={css(styles.settingInfo)}>
							<div className={css(styles.settingTitle)}>Default max tickets / agent</div>
							<div className={css(styles.settingDesc)}>
								New tickets are assigned up to this limit; teammates at or above it stop receiving new tickets.
							</div>
						</div>
						<Input
							type='number'
							min={1}
							value={draft.defaultMaxTickets}
							onChange={e => {
								const val = parseInt(e.target.value, 10);
								if (!isNaN(val) && val > 0) {
									updateDraft({ defaultMaxTickets: val });
								}
							}}
							styles={[styles.numberInput]}
						/>
					</div>

					<div className={css(styles.saveRow)}>
						<Button onClick={handleSaveRoutingSettings} disabled={updateMailboxMutation.isPending || !isDirty}>
							{updateMailboxMutation.isPending ? 'Saving...' : 'Save Changes'}
						</Button>
						{updateMailboxMutation.isSuccess && !isDirty && (
							<span className={css(styles.successMsg)}>Settings saved.</span>
						)}
						{updateMailboxMutation.isError && (
							<span className={css(styles.errorMsg)}>
								{getApiErrorMessage(updateMailboxMutation.error, 'Failed to save settings.')}
							</span>
						)}
					</div>
				</>
			)}
		</>
	);
}

function useRoutingDraft(mailbox: ISupportMailbox) {
	const [draft, setDraft] = React.useState<IRoutingDraft>(() => getRoutingDraft(mailbox));
	const [isDirty, setIsDirty] = React.useState(false);
	const isDirtyRef = React.useRef(false);
	const hydratedMailboxIdRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		const isSwitchingMailboxes = hydratedMailboxIdRef.current !== mailbox.id;
		if (!isSwitchingMailboxes && isDirtyRef.current) return;

		hydratedMailboxIdRef.current = mailbox.id;
		isDirtyRef.current = false;
		setDraft(getRoutingDraft(mailbox));
		setIsDirty(false);
	}, [mailbox]);

	const updateDraft = (patch: Partial<IRoutingDraft>) => {
		isDirtyRef.current = true;
		setIsDirty(true);
		setDraft(current => ({ ...current, ...patch }));
	};

	const markClean = () => {
		isDirtyRef.current = false;
		setIsDirty(false);
	};

	return { draft, isDirty, updateDraft, markClean };
}

function getRoutingDraft(mailbox: ISupportMailbox): IRoutingDraft {
	return {
		isRoutingEnabled: mailbox.isRoutingEnabled ?? false,
		assignmentMethod: mailbox.assignmentMethod ?? AssignmentMethod.Balanced,
		defaultMaxTickets: mailbox.defaultMaxTicketsPerAgent ?? 25,
	};
}

function buildRoutingMailboxPatch(draft: IRoutingDraft): RoutingMailboxPatch {
	return {
		isRoutingEnabled: draft.isRoutingEnabled,
		assignmentMethod: draft.assignmentMethod,
		defaultMaxTicketsPerAgent: draft.defaultMaxTickets,
	};
}
